/**
 * 文件夹和项目管理器
 * 负责管理文件夹和项目数据，提供获取、更新等功能
 * @author 杨俊朗
 */
import { sidebarFolders } from "@/services/sidebar-folders";
import { sidebarFolderProjects } from "@/services/sidebar-folder-projects";
import { sidebarFolder } from "@/services/sidebar-folder";
import { sidebarFolderDelete } from "@/services/sidebar-folder-delete";
import { sidebarFolderRename } from "@/services/sidebar-folder-rename";
import { sidebarFolderSort } from "@/services/sidebar-folder-sort";
import { sidebarProject } from "@/services/sidebar-project";
import { sidebarProjectRename } from "@/services/sidebar-project-rename";
import { sidebarProjectDelete } from "@/services/sidebar-project-delete";
import { sidebarProjectMove } from "@/services/sidebar-project-move";
import { videoVideosByFolderProject } from "@/services/video-videos-by-folder-project";
import { canUseWindow } from "@/utils/canuseWindow";
import { iamTeamManager } from "./iam-team-manager";
import { logger } from "@/utils/logger";
import { useEffect, useState } from "react";
import { useLocation, useMatch, useParams } from "react-router-dom";
import { useDownload } from "@/hooks/use-download";
import analytics from "@/utils/analytics";

/**
 * ProductManager - 文件夹和项目管理器
 * 基于单例模式，提供文件夹和项目数据的管理功能
 */
export class CreationProductManager {
  private static instance: CreationProductManager;
  // 缓存键名
  private readonly storageKey = "MIUTA_FOLDERS_PROJECTS";
  // 未分类
  public readonly uncategorized = "uncategorized";
  // 文件夹前缀
  public readonly folderPrefix = "folder_";
  // 项目前缀
  public readonly projectPrefix = "project_";
  // 缓存数据
  private cachedData: CachedData = {
    teamCode: "",
    workspaceId: "",
    folders: [],
    projectsMap: {
      [this.uncategorized]: [],
    },
    folderOriginIdMap: {},
    projectOriginIdMap: {},
    expandedFolders: [],
    timestamp: 0,
  };

  /**
   * 获取单例实例
   */
  static getInstance(): CreationProductManager {
    if (!CreationProductManager.instance) {
      CreationProductManager.instance = new CreationProductManager();
    }
    return CreationProductManager.instance;
  }

  /**
   * 初始化 - 从 localStorage 读取缓存数据
   * 缓存的是最近一次访问的Product 切换Product则内容进行切换
   */
  public async init(loading = true) {
    // 从缓存中获取数据
    let cached: Record<string, CachedData> = {};
    try {
      if (localStorage.getItem(this.storageKey)) {
        cached = JSON.parse(localStorage.getItem(this.storageKey)) as Record<string, CachedData>;
      }
    } catch (e) {
      localStorage.removeItem(this.storageKey);
      logger.error("creationProduct data loaded from cache: error:", e);
      throw e;
    }
    const data = cached[`${iamTeamManager.getTeamCode()}_${iamTeamManager.getWorkspaceId()}`];
    if (data) {
      // 缓存优先
      this.update({
        teamCode: data.teamCode,
        workspaceId: data.workspaceId,
        folders: data.folders,
        projectsMap: data.projectsMap,
        folderOriginIdMap: data.folderOriginIdMap,
        projectOriginIdMap: data.projectOriginIdMap,
        expandedFolders: new Set(data.expandedFolders),
      });
      // 后台同步数据
      this.fetch();
      logger.info("creationProduct data loaded from cache:", data);
      return;
    }
    // 缓存不存在 初始化数据
    loading && this.triggerLoading(true);
    try {
      // 重新获取数据
      await this.fetch();
    } catch (error) {
      throw error;
    } finally {
      this.triggerLoading(false);
    }
  }

  /**
   * 从后台获取数据
   */
  public async fetch() {
    // 当前团队
    const teamCode = iamTeamManager.getTeamCode();
    // 当前工作空间
    const workspaceId = iamTeamManager.getWorkspaceId();
    // 新数据
    const newFolders: Folder[] = [];
    const newProjectsMap: Record<string, Project[]> = {
      [this.uncategorized]: [],
    };
    const newExpandedFolders: Set<string> = new Set();
    const newFolderOriginIdMap: Record<string, string> = {};
    const newProjectOriginIdMap: Record<string, string> = {};
    // 后台获取文件夹列表
    const responseFolders = await sidebarFolders({
      workspaceId,
      currentPage: 1,
      pageSize: 1000,
      switchWorkspace: true,
    });
    const newFoldersData = responseFolders.folders
      // 过滤未分类文件夹
      .filter((folder) => !folder.isUncategorized)
      .sort((a, b) => {
        return b.sortOrder - a.sortOrder;
      });
    // 兼容处理 把文件夹下项目迁移到未分类
    if (newFoldersData.length) {
      // 获取未分类项目列表
      const responseProjects = await sidebarFolderProjects({
        workspaceId,
        folderId: "",
        currentPage: 1,
        pageSize: 1000,
      });
      const newProjectsData = responseProjects.projects.sort((a, b) => {
        return b.sortOrder - a.sortOrder;
      });
      for (let i = 0; i < newFoldersData.length; i++) {
        // 有项目执行迁移 没有项目执行删除
        if (newFoldersData[i].projectNum > 0) {
          const res = await sidebarFolderProjects({
            workspaceId,
            folderId: newFoldersData[i].id,
            currentPage: 1,
            pageSize: 1000,
          });
          const data = res.projects.sort((a, b) => b.sortOrder - a.sortOrder);
          // 依次迁移到未分类
          for (let i = 0; i < data.length; i++) {
            newProjectsData.push(data[i]);
            await sidebarProjectMove({
              projectId: data[i].id,
              toFolderId: null,
              sortDetail: newProjectsData.map((project, index) => ({
                id: project.id,
                sortOrder: newProjectsData.length - index,
              })),
            });
          }
        } else {
          await sidebarFolderDelete(newFoldersData[i].id);
        }
      }
    }
    // for (let i = 0; i < newFoldersData.length; i++) {
    //   const folder = newFoldersData[i];
    //   const id = `${this.folderPrefix}${folder.id}`;
    //   newFolderOriginIdMap[id] = folder.id;
    //   newFolders.push({
    //     ...folder,
    //     id,
    //     sortOrder: folder.sortOrder,
    //     createdAt: folder.createdAt,
    //     latestVideoImage: folder.latestVideoImage,
    //   });
    //   newProjectsMap[id] = [];
    // }
    // 后台获取未分类项目
    const responseProjects = await sidebarFolderProjects({
      workspaceId,
      folderId: "",
      currentPage: 1,
      pageSize: 1000,
    });
    const newProjectsData = responseProjects.projects.sort((a, b) => {
      return b.sortOrder - a.sortOrder;
    });
    for (let i = 0; i < newProjectsData.length; i++) {
      const project = newProjectsData[i];
      const id = `${this.projectPrefix}${project.id}`;
      newProjectOriginIdMap[id] = project.id;
      newProjectsMap[this.uncategorized].push({
        ...project,
        id,
      });
    }
    // 后台获取文件夹下项目
    // await Promise.all(
    //   // 请求所有有项目的文件夹
    //   newFoldersData
    //     .filter((folder) => folder.projectNum > 0)
    //     .map(async (folder) => {
    //       const res = await sidebarFolderProjects({
    //         workspaceId: this.cachedData.productId,
    //         folderId: folder.id,
    //         currentPage: 1,
    //         pageSize: 1000,
    //       });
    //       const data = res.projects.sort((a, b) => b.sortOrder - a.sortOrder);
    //       for (let i = 0; i < data.length; i++) {
    //         const project = data[i];
    //         const id = `${this.projectPrefix}${project.id}`;
    //         newProjectOriginIdMap[id] = project.id;
    //         newProjectsMap[`${this.folderPrefix}${folder.id}`].push({
    //           ...project,
    //           id,
    //         });
    //       }
    //     })
    // );
    // 如果有缓存数据，进行智能合并
    if (this.cachedData) {
      // 1. 合并 expandedFolders - 保留现有展开状态，移除不存在的文件夹
      const existingExpandedFolders = new Set(
        this.cachedData.expandedFolders
      );
      const newFolderIds = new Set([
        ...newFolders.map((f) => f.id),
        this.uncategorized,
      ]);

      // 保留仍然存在的文件夹的展开状态
      existingExpandedFolders.forEach((folderId) => {
        if (newFolderIds.has(folderId)) {
          newExpandedFolders.add(folderId);
        }
      });
    }
    this.update({
      teamCode,
      workspaceId,
      folders: newFolders,
      projectsMap: newProjectsMap,
      folderOriginIdMap: newFolderOriginIdMap,
      projectOriginIdMap: newProjectOriginIdMap,
      expandedFolders: newExpandedFolders,
    });
  }

  /**
   * 创建新文件夹
   */
  async newFolder(tempId: string, folderName: string) {
    if (!this.cachedData) {
      return;
    }
    // 临时id
    const id = `${this.folderPrefix}${tempId}`;
    // 创建临时内容
    const newFolder: Folder = {
      id,
      name: folderName,
      // 新文件夹默认放在第一个
      sortOrder: this.cachedData.folders[0]
        ? this.cachedData.folders[0].sortOrder + 1
        : 1,
      createdAt: new Date().toISOString(),
      latestVideoImage: "",
    };
    const newProjects = [];
    const folders = [newFolder, ...this.cachedData.folders];
    const projectsMap = {
      ...this.cachedData.projectsMap,
      [id]: newProjects,
    };
    // 更新内容
    this.update({
      teamCode: this.cachedData.teamCode,
      workspaceId: this.cachedData.workspaceId,
      folders,
      projectsMap,
      folderOriginIdMap: this.cachedData.folderOriginIdMap,
      projectOriginIdMap: this.cachedData.projectOriginIdMap,
      expandedFolders: new Set(this.cachedData.expandedFolders),
    });
    // 同步数据
    try {
      const res = await sidebarFolder("New Folder", this.cachedData.workspaceId);
      if (!this.cachedData) {
        return;
      }
      // 标记id 临时内容变为真实内容
      const folderOriginIdMap = {
        ...this.cachedData.folderOriginIdMap,
        [id]: res.folder.id,
      };
      this.update({
        teamCode: this.cachedData.teamCode,
        workspaceId: this.cachedData.workspaceId,
        folderOriginIdMap,
        folders: this.cachedData.folders,
        projectsMap: this.cachedData.projectsMap,
        projectOriginIdMap: this.cachedData.projectOriginIdMap,
        expandedFolders: new Set(this.cachedData.expandedFolders),
      });
      console.log("create folder success", res);
      return res.folder.id;
    } catch (error) {
      console.error("create folder error", error);
      // 回退数据
      const folders = this.cachedData.folders.filter((f) => f.id !== id);
      const projectsMap = {
        ...this.cachedData.projectsMap,
      };
      delete projectsMap[id];
      this.update({
        teamCode: this.cachedData.teamCode,
        workspaceId: this.cachedData.workspaceId,
        folders,
        projectsMap,
        folderOriginIdMap: this.cachedData.folderOriginIdMap,
        projectOriginIdMap: this.cachedData.projectOriginIdMap,
        expandedFolders: new Set(this.cachedData.expandedFolders),
      });
      // 继续抛出错误
      throw error;
    }
  }

  // 创建新项目
  async newProject(tempId: string, projectName: string, folder: Folder | null) {
    if (!this.cachedData) {
      return;
    }
    // 项目id
    const pid = folder ? folder.id : this.uncategorized;
    const id = `${this.projectPrefix}${tempId}`;
    // 创建临时内容
    const newProject: Project = {
      id,
      name: projectName,
      sortOrder: this.cachedData.projectsMap[pid][0]
        ? this.cachedData.projectsMap[pid][0].sortOrder + 1
        : 1,
      createdAt: new Date().toISOString(),
      latestVideoImage: "",
    };
    const newProjects = [newProject, ...this.cachedData.projectsMap[pid]];
    const projectsMap = {
      ...this.cachedData.projectsMap,
      [pid]: newProjects,
    };
    // 展开所在文件夹
    const expandedFolders = new Set(this.cachedData.expandedFolders);
    expandedFolders.add(pid);
    // 更新内容
    this.update({
      teamCode: this.cachedData.teamCode,
      workspaceId: this.cachedData.workspaceId,
      projectsMap,
      expandedFolders,
      folders: this.cachedData.folders,
      folderOriginIdMap: this.cachedData.folderOriginIdMap,
      projectOriginIdMap: this.cachedData.projectOriginIdMap,
    });
    // 同步数据
    try {
      const res = await sidebarProject({
        name: "New Project",
        folderId: folder ? this.cachedData.folderOriginIdMap[folder.id] : null,
        workspaceId: this.cachedData.workspaceId,
      });
      if (!this.cachedData) {
        return;
      }
      // 标记id 临时内容变为真实内容
      const projectOriginIdMap = {
        ...this.cachedData.projectOriginIdMap,
        [id]: res.project.id,
      };
      this.update({
        teamCode: this.cachedData.teamCode,
        workspaceId: this.cachedData.workspaceId,
        projectOriginIdMap,
        folders: this.cachedData.folders,
        projectsMap: this.cachedData.projectsMap,
        folderOriginIdMap: this.cachedData.folderOriginIdMap,
        expandedFolders: new Set(this.cachedData.expandedFolders),
      });
      console.log("create project success", res);
      return res.project.id;
    } catch (error) {
      console.error("create project error", error);
      // 回退数据
      const projects = this.cachedData.projectsMap[pid].filter(
        (p) => p.id !== id
      );
      const projectsMap = {
        ...this.cachedData.projectsMap,
        [pid]: projects,
      };
      this.update({
        teamCode: this.cachedData.teamCode,
        workspaceId: this.cachedData.workspaceId,
        projectsMap,
        projectOriginIdMap: this.cachedData.projectOriginIdMap,
        folders: this.cachedData.folders,
        folderOriginIdMap: this.cachedData.folderOriginIdMap,
        expandedFolders: new Set(this.cachedData.expandedFolders),
      });
      // 继续抛出错误
      throw error;
    }
  }

  /**
   * 文件夹重命名
   */
  async renameFolder(folder: Folder, name: string) {
    if (!this.cachedData) {
      return;
    }
    const oldName = folder.name;
    const folders = [...this.cachedData.folders];
    for (const f of folders) {
      if (f.id === folder.id) {
        f.name = name;
      }
    }
    // 乐观更新
    this.update({
      teamCode: this.cachedData.teamCode,
      workspaceId: this.cachedData.workspaceId,
      folders,
      projectsMap: this.cachedData.projectsMap,
      folderOriginIdMap: this.cachedData.folderOriginIdMap,
      projectOriginIdMap: this.cachedData.projectOriginIdMap,
      expandedFolders: new Set(this.cachedData.expandedFolders),
    });
    try {
      const res = await sidebarFolderRename({
        folderId: this.cachedData.folderOriginIdMap[folder.id],
        name,
      });
      console.log("rename folder success", res);
    } catch (error) {
      console.error("rename folder error", error);
      // 回退数据
      const folders = [...this.cachedData.folders];
      for (const f of folders) {
        if (f.id === folder.id) {
          f.name = oldName;
        }
      }
      this.update({
        teamCode: this.cachedData.teamCode,
        workspaceId: this.cachedData.workspaceId,
        folders,
        projectsMap: this.cachedData.projectsMap,
        folderOriginIdMap: this.cachedData.folderOriginIdMap,
        projectOriginIdMap: this.cachedData.projectOriginIdMap,
        expandedFolders: new Set(this.cachedData.expandedFolders),
      });
      // 继续抛出错误
      throw error;
    }
  }

  /**
   * 文件夹删除事件
   */
  async deleteFolder(folder: Folder) {
    if (!this.cachedData) {
      return;
    }
    const index = this.cachedData.folders.findIndex((f) => f.id === folder.id);
    const folders = [...this.cachedData.folders];
    folders.splice(index, 1);
    // 乐观更新
    this.update({
      teamCode: this.cachedData.teamCode,
      workspaceId: this.cachedData.workspaceId,
      folders,
      projectsMap: this.cachedData.projectsMap,
      folderOriginIdMap: this.cachedData.folderOriginIdMap,
      projectOriginIdMap: this.cachedData.projectOriginIdMap,
      expandedFolders: new Set(this.cachedData.expandedFolders),
    });
    try {
      const res = await sidebarFolderDelete(
        this.cachedData.folderOriginIdMap[folder.id]
      );
      if (!this.cachedData) {
        return;
      }
      // 删除成功后，删除项目映射(完全删除数据)
      const projectsMap = { ...this.cachedData.projectsMap };
      const projects = projectsMap[folder.id];
      const projectOriginIdMap = { ...this.cachedData.projectOriginIdMap };
      for (const p of projects) {
        delete projectOriginIdMap[p.id];
      }
      delete projectsMap[folder.id];
      const folderOriginIdMap = { ...this.cachedData.folderOriginIdMap };
      delete folderOriginIdMap[folder.id];
      const expandedFolders = this.cachedData.expandedFolders.filter(
        (f) => f !== folder.id
      );
      this.update({
        teamCode: this.cachedData.teamCode,
        workspaceId: this.cachedData.workspaceId,
        folders,
        projectsMap,
        folderOriginIdMap,
        projectOriginIdMap,
        expandedFolders: new Set(expandedFolders),
      });
      console.log("delete folder success", res);
    } catch (error) {
      console.error("delete folder error", error);
      const folders = [...this.cachedData.folders];
      folders.splice(index, 0, folder);
      // 回退数据
      this.update({
        teamCode: this.cachedData.teamCode,
        workspaceId: this.cachedData.workspaceId,
        folders,
        projectsMap: this.cachedData.projectsMap,
        projectOriginIdMap: this.cachedData.projectOriginIdMap,
        folderOriginIdMap: this.cachedData.folderOriginIdMap,
        expandedFolders: new Set(this.cachedData.expandedFolders),
      });
      // 继续抛出错误
      throw error;
    }
  }

  /**
   * 项目重命名
   */
  async renameProject(project: Project, name: string, folder: Folder | null) {
    if (!this.cachedData) {
      return;
    }
    const pid = folder ? folder.id : this.uncategorized;
    const oldName = project.name;
    const projects = [...this.cachedData.projectsMap[pid]];
    for (const p of projects) {
      if (p.id === project.id) {
        p.name = name;
      }
    }
    const projectsMap = {
      ...this.cachedData.projectsMap,
      [pid]: projects,
    };
    // 乐观更新
    this.update({
      teamCode: this.cachedData.teamCode,
      workspaceId: this.cachedData.workspaceId,
      projectsMap,
      projectOriginIdMap: this.cachedData.projectOriginIdMap,
      folders: this.cachedData.folders,
      folderOriginIdMap: this.cachedData.folderOriginIdMap,
      expandedFolders: new Set(this.cachedData.expandedFolders),
    });
    try {
      await sidebarProjectRename({
        projectId: this.cachedData.projectOriginIdMap[project.id],
        name,
      });
      console.log("rename project success");
    } catch (error) {
      console.error("rename project error", error);
      // 失败回退处理
      const projects = [...this.cachedData.projectsMap[pid]];
      for (const p of projects) {
        if (p.id === project.id) {
          p.name = oldName;
        }
      }
      const projectsMap = {
        ...this.cachedData.projectsMap,
        [pid]: projects,
      };
      // 回退数据
      this.update({
        teamCode: this.cachedData.teamCode,
        workspaceId: this.cachedData.workspaceId,
        projectsMap,
        projectOriginIdMap: this.cachedData.projectOriginIdMap,
        folders: this.cachedData.folders,
        folderOriginIdMap: this.cachedData.folderOriginIdMap,
        expandedFolders: new Set(this.cachedData.expandedFolders),
      });
      // 继续抛出错误
      throw error;
    }
  }

  /**
   * 项目删除
   */
  async deleteProject(project: Project, folder: Folder | null) {
    const pid = folder ? folder.id : this.uncategorized;
    const index = this.cachedData.projectsMap[pid].findIndex(
      (p) => p.id === project.id
    );
    const projects = [...this.cachedData.projectsMap[pid]];
    projects.splice(index, 1);
    const projectsMap = {
      ...this.cachedData.projectsMap,
      [pid]: projects,
    };
    // 乐观更新
    this.update({
      teamCode: this.cachedData.teamCode,
      workspaceId: this.cachedData.workspaceId,
      projectsMap,
      projectOriginIdMap: this.cachedData.projectOriginIdMap,
      folders: this.cachedData.folders,
      folderOriginIdMap: this.cachedData.folderOriginIdMap,
      expandedFolders: new Set(this.cachedData.expandedFolders),
    });
    try {
      const res = await sidebarProjectDelete(
        this.cachedData.projectOriginIdMap[project.id]
      );
      // 删除成功后，删除项目映射(完全删除数据)
      const projectOriginIdMap = { ...this.cachedData.projectOriginIdMap };
      delete projectOriginIdMap[project.id];
      this.update({
        teamCode: this.cachedData.teamCode,
        workspaceId: this.cachedData.workspaceId,
        projectOriginIdMap,
        folders: this.cachedData.folders,
        projectsMap: this.cachedData.projectsMap,
        folderOriginIdMap: this.cachedData.folderOriginIdMap,
        expandedFolders: new Set(this.cachedData.expandedFolders),
      });
      console.log("delete project success", res);
    } catch (error) {
      console.error("delete project error", error);
      // 失败回退处理
      const projects = [...this.cachedData.projectsMap[pid]];
      projects.splice(index, 0, project);
      const projectsMap = {
        ...this.cachedData.projectsMap,
        [pid]: projects,
      };
      // 回退数据
      this.update({
        teamCode: this.cachedData.teamCode,
        workspaceId: this.cachedData.workspaceId,
        projectsMap,
        projectOriginIdMap: this.cachedData.projectOriginIdMap,
        folders: this.cachedData.folders,
        folderOriginIdMap: this.cachedData.folderOriginIdMap,
        expandedFolders: new Set(this.cachedData.expandedFolders),
      });
    }
  }

  /**
   * 文件夹重新排序
   */
  async reorderFolders(reorderedContainerIds: string[]) {
    const folders = [...this.cachedData.folders];
    const reorderedFolders: Folder[] = [];
    reorderedContainerIds.forEach((folderId) => {
      const folder = folders.find((f) => f.id === folderId);
      if (folder) {
        reorderedFolders.push(folder);
      }
    });
    // 乐观更新
    this.update({
      teamCode: this.cachedData.teamCode,
      workspaceId: this.cachedData.workspaceId,
      folders: reorderedFolders,
      projectsMap: this.cachedData.projectsMap,
      projectOriginIdMap: this.cachedData.projectOriginIdMap,
      folderOriginIdMap: this.cachedData.folderOriginIdMap,
      expandedFolders: new Set(this.cachedData.expandedFolders),
    });
    try {
      await sidebarFolderSort({
        folderDetail: reorderedContainerIds
          .filter((item) => this.cachedData.folderOriginIdMap[item])
          .map((item, index) => ({
            id: this.cachedData.folderOriginIdMap[item],
            sortOrder: reorderedContainerIds.length - index,
          })),
      });
    } catch (error) {
      console.error("reorder folders error", error);
      // 失败回退处理
      this.update({
        teamCode: this.cachedData.teamCode,
        workspaceId: this.cachedData.workspaceId,
        folders,
        projectsMap: this.cachedData.projectsMap,
        projectOriginIdMap: this.cachedData.projectOriginIdMap,
        folderOriginIdMap: this.cachedData.folderOriginIdMap,
        expandedFolders: new Set(this.cachedData.expandedFolders),
      });
      // 继续抛出错误
      throw error;
    }
  }

  /**
   * 项目重新排序
   */
  async reorderProjects(
    id: string,
    folderId: string,
    reorderedItemIds: string[]
  ) {
    let oldFolderId = this.uncategorized;
    for (const folder of this.cachedData.folders) {
      const has = this.cachedData.projectsMap[folder.id].some((p) => {
        return p.id === id;
      });
      if (has) {
        oldFolderId = folder.id;
        break;
      }
    }
    const oldFoldersProjects = [...this.cachedData.projectsMap[oldFolderId]];
    const newFoldersProjects = [...this.cachedData.projectsMap[folderId]];
    const reorderOldProjects = oldFoldersProjects.filter((p) => {
      return p.id !== id;
    });
    const reorderedProjects: Project[] = [];
    for (const projectId of reorderedItemIds) {
      if (projectId === id) {
        const project = oldFoldersProjects.find((p) => p.id === id);
        if (project) {
          reorderedProjects.push(project);
        }
      } else {
        const project = newFoldersProjects.find((p) => p.id === projectId);
        if (project) {
          reorderedProjects.push(project);
        }
      }
    }
    const projectsMap = {
      ...this.cachedData.projectsMap,
      [oldFolderId]: reorderOldProjects,
      [folderId]: reorderedProjects,
    };
    // 排序到一个折叠的文件夹则默认展开文件夹
    const expandedFolders = new Set(this.cachedData.expandedFolders);
    expandedFolders.add(folderId);
    // 乐观更新
    this.update({
      teamCode: this.cachedData.teamCode,
      workspaceId: this.cachedData.workspaceId,
      projectsMap,
      expandedFolders,
      projectOriginIdMap: this.cachedData.projectOriginIdMap,
      folders: this.cachedData.folders,
      folderOriginIdMap: this.cachedData.folderOriginIdMap,
    });
    try {
      await sidebarProjectMove({
        projectId: this.cachedData.projectOriginIdMap[id],
        toFolderId: this.cachedData.folderOriginIdMap[folderId],
        sortDetail: reorderedItemIds
          .filter((item) => this.cachedData.projectOriginIdMap[item])
          .map((item, index) => ({
            id: this.cachedData.projectOriginIdMap[item],
            sortOrder: reorderedItemIds.length - index,
          })),
      });
    } catch (error) {
      console.error("reorder projects error", error);
      // 失败回退处理
      const projectsMap = {
        ...this.cachedData.projectsMap,
        [oldFolderId]: oldFoldersProjects,
        [folderId]: newFoldersProjects,
      };
      this.update({
        teamCode: this.cachedData.teamCode,
        workspaceId: this.cachedData.workspaceId,
        projectsMap,
        folders: this.cachedData.folders,
        projectOriginIdMap: this.cachedData.projectOriginIdMap,
        folderOriginIdMap: this.cachedData.folderOriginIdMap,
        expandedFolders: new Set(this.cachedData.expandedFolders),
      });
      // 继续抛出错误
      throw error;
    }
  }

  // 项目展开
  toggleFolderExpand(folderId: string, expand = false) {
    const expandedFolders = new Set(this.cachedData.expandedFolders);
    if (expand) {
      expandedFolders.add(folderId);
    } else if (expandedFolders.has(folderId)) {
      expandedFolders.delete(folderId);
    } else {
      expandedFolders.add(folderId);
    }
    this.update({
      teamCode: this.cachedData.teamCode,
      workspaceId: this.cachedData.workspaceId,
      folders: this.cachedData.folders,
      projectsMap: this.cachedData.projectsMap,
      projectOriginIdMap: this.cachedData.projectOriginIdMap,
      folderOriginIdMap: this.cachedData.folderOriginIdMap,
      expandedFolders,
    });
  }

  // 获取项目下的所有视频下载链接
  async getProjectVideos(projectId: string) {
    const projectOriginIdMap = this.getProjectOriginIdMap();
    const result = await videoVideosByFolderProject({
      projectId: projectOriginIdMap[projectId],
      currentPage: 1,
      pageSize: 1000,
    });
    if (!result.videos) {
      return [];
    }
    return result.videos.filter((item) => item.videoUrl);
  }

  /**
   * 更新缓存数据
   */
  update(value: UpdateData): void {
    const cachedData: CachedData = {
      ...value,
      expandedFolders: Array.from(value.expandedFolders),
      timestamp: Date.now(),
    };
    // 更新本地缓存
    if (value.teamCode ===  iamTeamManager.getTeamCode() && value.workspaceId === iamTeamManager.getWorkspaceId()) {
      this.cachedData = cachedData;
    }
    try {
      const cached = localStorage.getItem(this.storageKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        localStorage.setItem(this.storageKey, JSON.stringify({
          ...parsed,
          [`${value.teamCode}_${value.workspaceId}`]: cachedData,
        }));
      } else {
        localStorage.setItem(this.storageKey, JSON.stringify({
          [`${value.teamCode}_${value.workspaceId}`]: cachedData,
        }));
      }
    } catch (error) {
      console.error("缓存存储失败:", error);
      // 如果存储失败（可能是空间不足），清理缓存
      localStorage.removeItem(this.storageKey);
    }

    // 触发自定义事件
    this.dispatch();
  }

  /**
   * 获取缓存数据
   */
  get(): CachedData | null {
    return this.cachedData || {
      teamCode: "",
      workspaceId: "",
      folders: [],
      projectsMap: {
        [this.uncategorized]: [],
      },
      folderOriginIdMap: {},
      projectOriginIdMap: {},
      expandedFolders: [],
      timestamp: 0,
    };
  }

  /**
   * 清除缓存数据
   */
  clear(): void {
    this.cachedData = {
      teamCode: "",
      workspaceId: "",
      folders: [],
      projectsMap: {
        [this.uncategorized]: [],
      },
      folderOriginIdMap: {},
      projectOriginIdMap: {},
      expandedFolders: [],
      timestamp: 0,
    };
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem(this.storageKey);
    }
    this.dispatch();
  }

  /**
   * 获取文件夹列表
   */
  getFolders(): Folder[] {
    return this.cachedData?.folders || [];
  }

  /**
   * 获取项目映射
   */
  getProjectsMap(): Record<string, Project[]> {
    return this.cachedData?.projectsMap || {};
  }

  /**
   * 获取文件夹原始ID映射（带前缀验证）
   */
  getFolderOriginIdMap(): Record<string, string> {
    const originalMap = this.cachedData?.folderOriginIdMap || {};

    return new Proxy(originalMap, {
      get: (target, prop: string) => {
        if (typeof prop === "string" && !prop.startsWith(this.folderPrefix)) {
          throw new Error(
            `Invalid folder ID prefix. Expected "${this.folderPrefix}" but got "${prop}". Folder IDs must start with "${this.folderPrefix}".`
          );
        }
        return target[prop];
      },
      set: (target, prop: string, value) => {
        if (typeof prop === "string" && !prop.startsWith(this.folderPrefix)) {
          throw new Error(
            `Invalid folder ID prefix. Expected "${this.folderPrefix}" but got "${prop}". Folder IDs must start with "${this.folderPrefix}".`
          );
        }
        target[prop] = value;
        return true;
      },
      has: (target, prop: string) => {
        if (typeof prop === "string" && !prop.startsWith(this.folderPrefix)) {
          throw new Error(
            `Invalid folder ID prefix. Expected "${this.folderPrefix}" but got "${prop}". Folder IDs must start with "${this.folderPrefix}".`
          );
        }
        return prop in target;
      },
      ownKeys: (target) => {
        return Object.keys(target);
      },
      getOwnPropertyDescriptor: (target, prop) => {
        return Object.getOwnPropertyDescriptor(target, prop);
      },
      deleteProperty: (target, prop: string) => {
        if (typeof prop === "string" && !prop.startsWith(this.folderPrefix)) {
          throw new Error(
            `Invalid folder ID prefix. Expected "${this.folderPrefix}" but got "${prop}". Folder IDs must start with "${this.folderPrefix}".`
          );
        }
        delete target[prop];
        return true;
      },
    });
  }

  /**
   * 获取项目原始ID映射（带前缀验证）
   */
  getProjectOriginIdMap(): Record<string, string> {
    const originalMap = this.cachedData?.projectOriginIdMap || {};

    return new Proxy(originalMap, {
      get: (target, prop: string) => {
        if (typeof prop === "string" && !prop.startsWith(this.projectPrefix)) {
          throw new Error(
            `Invalid project ID prefix. Expected "${this.projectPrefix}" but got "${prop}". Project IDs must start with "${this.projectPrefix}".`
          );
        }
        return target[prop];
      },
      set: (target, prop: string, value) => {
        if (typeof prop === "string" && !prop.startsWith(this.projectPrefix)) {
          throw new Error(
            `Invalid project ID prefix. Expected "${this.projectPrefix}" but got "${prop}". Project IDs must start with "${this.projectPrefix}".`
          );
        }
        target[prop] = value;
        return true;
      },
      has: (target, prop: string) => {
        if (typeof prop === "string" && !prop.startsWith(this.projectPrefix)) {
          throw new Error(
            `Invalid project ID prefix. Expected "${this.projectPrefix}" but got "${prop}". Project IDs must start with "${this.projectPrefix}".`
          );
        }
        return prop in target;
      },
      ownKeys: (target) => {
        return Object.keys(target);
      },
      getOwnPropertyDescriptor: (target, prop) => {
        return Object.getOwnPropertyDescriptor(target, prop);
      },
      deleteProperty: (target, prop: string) => {
        if (typeof prop === "string" && !prop.startsWith(this.projectPrefix)) {
          throw new Error(
            `Invalid project ID prefix. Expected "${this.projectPrefix}" but got "${prop}". Project IDs must start with "${this.projectPrefix}".`
          );
        }
        delete target[prop];
        return true;
      },
    });
  }

  /**
   * 获取展开的文件夹列表
   */
  getExpandedFolders(): Set<string> {
    return new Set(this.cachedData?.expandedFolders || []);
  }

  /**
   * 获取缓存时间戳
   */
  getTimestamp(): number | undefined {
    return this.cachedData?.timestamp;
  }

  /**
   * 获取未分类项目key
   */
  getUncategorized(): string {
    return this.uncategorized;
  }

  /**
   * 获取文件夹前缀
   */
  getFolderPrefix(): string {
    return this.folderPrefix;
  }

  /**
   * 获取项目前缀
   */
  getProjectPrefix(): string {
    return this.projectPrefix;
  }

  /**
   * 刷新缓存（重新从存储读取）
   */
  refresh(): void {
    this.init();
  }

  /**
   * 触发自定义事件
   */
  private dispatch(): void {
    if (!canUseWindow()) {
      return;
    }
    const event = new CustomEvent<CachedData | null>(this.storageKey, {
      detail: this.cachedData,
    });
    window.dispatchEvent(event);
  }

  /**
   * 监听方法
   */
  listen(callback: (e: CustomEvent<CachedData | null>) => void) {
    if (!canUseWindow()) {
      return () => {};
    }
    window.addEventListener(this.storageKey, callback);

    return () => {
      window.removeEventListener(this.storageKey, callback);
    };
  }

  /**
   * 加载状态监听
   */
  loading(callback: (e: CustomEvent<boolean>) => void) {
    if (!canUseWindow()) {
      return () => {};
    }
    window.addEventListener(`${this.storageKey}:loading`, callback);

    return () => {
      window.removeEventListener(`${this.storageKey}:loading`, callback);
    };
  }

  /**
   * 加载状态触发
   */
  triggerLoading(status: boolean) {
    if (!canUseWindow()) {
      return;
    }
    const event = new CustomEvent<boolean>(`${this.storageKey}:loading`, {
      detail: status,
    });
    window.dispatchEvent(event);
  }
}

export const useCreationProductData = () => {
  const [data, setData] = useState<CachedData>(creationProductManager.get());

  useEffect(() => {
    return creationProductManager.listen((e) => {
      setData(e.detail);
    });
  }, []);

  return {
    ...data,
    expandedFolders: new Set(data.expandedFolders),
  };
};

export const useCreationProductActiveFolder = () => {
  const location = useLocation();
  const params = useParams();
  const isProduct = useMatch("/creation/product/");
  const isFolder = useMatch("/creation/folder/:id/");
  const isProject = useMatch("/creation/project/:id/");
  const data = useCreationProductData();
  const [activeFolder, setActiveFolder] = useState<Folder | null>(null);

  useEffect(() => {
    if (isFolder) {
      // 临时路径转真实路径
      if (location.state?.id) {
        const folder = data.folders.find((item) => {
          return item.id === location.state.id;
        });
        setActiveFolder(folder);
        return;
      }
      if (params.id) {
        for (const [key, value] of Object.entries(data.folderOriginIdMap)) {
          if (value === params.id) {
            setActiveFolder(data.folders.find((item) => item.id === key));
            return;
          }
        }
      }
      setActiveFolder(null);
      return;
    }
    setActiveFolder(null);
  }, [location, params, isProduct, isFolder, isProject, data]);

  return { activeFolder };
};

export const useCreationProductGetProjectFolder = () => {
  const getProjectFolder = (project: Project): Folder | null => {
    const data = creationProductManager.get();
    if (!data) {
      return null;
    }
    const { folders, projectsMap } = data;
    // 遍历所有文件夹，查找包含该项目的文件夹
    for (const folder of folders) {
      const folderProjects = projectsMap[folder.id] || [];
      if (folderProjects.some((p) => p.id === project.id)) {
        return folder;
      }
    }

    // 检查是否在未分类中
    const uncategorizedProjects =
      projectsMap[creationProductManager.uncategorized] || [];
    if (uncategorizedProjects.some((p) => p.id === project.id)) {
      return null; // 未分类返回null
    }

    // 项目不存在
    return null;
  };

  return { getProjectFolder };
};

export const useCreationProductActiveProject = () => {
  const location = useLocation();
  const params = useParams();
  const isProduct = useMatch("/creation/product/");
  const isFolder = useMatch("/creation/folder/:id/");
  const isProject = useMatch("/creation/project/:id/");
  const data = useCreationProductData();
  const { getProjectFolder } = useCreationProductGetProjectFolder();
  const [activeProject, setActiveProject] = useState<Project | null>(
    getActiveProject()
  );

  useEffect(() => {
    setActiveProject(getActiveProject());
  }, [location, params, isProduct, isFolder, isProject, data]);

  function getActiveProject() {
    if (isProject) {
      if (location.state?.id) {
        for (const projects of Object.values(data.projectsMap)) {
          if (projects.find((item) => item.id === location.state.id)) {
            return projects.find((item) => item.id === location.state.id);
          }
        }
      }
      if (params.id) {
        for (const [key, value] of Object.entries(data.projectOriginIdMap)) {
          if (value === params.id) {
            for (const projects of Object.values(data.projectsMap)) {
              if (projects.find((item) => item.id === key)) {
                return projects.find((item) => item.id === key);
              }
            }
          }
        }
      }
      return null;
    }
    return null;
  }

  const activeFolder = activeProject ? getProjectFolder(activeProject) : null;

  return { activeProject, activeFolder };
};

export const useCreationProudctActiveProjectOriginId = () => {
  const { activeProject, activeFolder } = useCreationProductActiveProject();
  const data = creationProductManager.get();

  const activeProjectOriginId = data.projectOriginIdMap[activeProject?.id];
  const activeFolderOriginId = data.folderOriginIdMap[activeFolder?.id];

  return {
    activeProjectOriginId,
    activeFolderOriginId,
  };
};

export const useCreationProductProjectVideosDownload = () => {
  const { downloadZip } = useDownload();

  const downloadProjectVideos = async (project: Project) => {
    const videos = await creationProductManager.getProjectVideos(project.id);
    analytics.trackCreationClickDownloadAll({
      projectId: project.id,
      videos: videos
        .filter((item) => item.videoStatus === 2 && !item.isUnusual)
        .map((item) => ({
          actorId: item.actor.id,
          voiceId: item.voice.id,
          script: item.inputText,
          videoId: item.id,
          videoURL: item.videoUrl,
          compressVideoURL: item.compressedVideoUrl,
        })),
    });
    const list = videos.map((item) => ({
      url: item.videoUrl,
      filename: `${item.actor.name}-${item.createdAt}.mp4`,
      imageUrl: item.actor.imageUrl,
    }));
    await downloadZip(list, `${project.name}.zip`);
  };

  return {
    downloadProjectVideos,
  };
};

export const creationProductHooks = {
  useData: useCreationProductData,
  useGetProjectFolder: useCreationProductGetProjectFolder,
  useActiveProject: useCreationProductActiveProject,
  useActiveProjectOriginId: useCreationProudctActiveProjectOriginId,
  useActiveFolder: useCreationProductActiveFolder,
  useProjectVideosDownload: useCreationProductProjectVideosDownload,
};

export const creationProductManager = CreationProductManager.getInstance();
